import { type SearchCommand } from "./search-engine";
import { useRouter } from "@tanstack/react-router";
import {
  Milk,
  WheatIcon,
  Package,
  Microscope,
  SearchIcon,
  Plus,
  FileText,
  Sparkles,
} from "lucide-react";
import { ANNOUNCEMENTS, latestAnnouncement } from "@brewbase/lib";
import { useAnnouncementStore } from "@/features/announcements/announcement-store";

/**
 * Search Commands Registry
 *
 * This module defines all available quick actions that can be triggered
 * through the search interface.
 */

// Navigation commands
export const createNavigationCommands = (
  router: ReturnType<typeof useRouter>,
): SearchCommand[] => [
  {
    id: "nav-fermentations",
    trigger: "fermentations",
    title: "Go to Fermentations",
    description: "View all fermentation groups and individual fermentations",
    keywords: ["ferment", "groups", "cultivation"],
    category: "navigate",
    action: () => router.navigate({ to: "/fermentations" }),
    icon: Milk,
  },
  {
    id: "nav-harvests",
    trigger: "harvests",
    title: "Go to Harvests",
    description: "View all harvest groups and processing operations",
    keywords: ["harvest", "processing", "downstream"],
    category: "navigate",
    action: () => router.navigate({ to: "/harvests" }),
    icon: WheatIcon,
  },
  {
    id: "nav-inventory",
    trigger: "inventory",
    title: "Go to Inventory",
    description: "Manage inventory items, resources, and solutions",
    keywords: ["inventory", "resources", "items", "stock"],
    category: "navigate",
    action: () => router.navigate({ to: "/inventory" }),
    icon: Package,
  },
  {
    id: "nav-analytics",
    trigger: "analytics",
    title: "Go to Analytics",
    description: "Sample queues, results and external lab orders",
    keywords: ["analytics", "tests", "analysis", "results", "lab", "queue"],
    category: "navigate",
    action: () => router.navigate({ to: "/analytics" }),
    icon: Microscope,
  },
];

// Creation commands
export const createCreationCommands = (
  router: ReturnType<typeof useRouter>,
): SearchCommand[] => [
  {
    id: "create-fermentation",
    trigger: "new fermentation",
    title: "Create New Fermentation",
    description: "Start a new fermentation experiment or production run",
    keywords: ["create", "new", "fermentation", "start", "begin"],
    category: "create",
    action: () =>
      router.navigate({
        to: "/fermentations",
        state: { openDialog: "create-fermentation" } as any,
      }),
    icon: Milk,
  },
  {
    id: "create-harvest",
    trigger: "new harvest",
    title: "Create New Harvest",
    description: "Plan a new harvest and downstream processing operation",
    keywords: ["create", "new", "harvest", "downstream", "processing"],
    category: "create",
    action: () =>
      router.navigate({
        to: "/harvests",
        state: { openDialog: "create-harvest" } as any,
      }),
    icon: WheatIcon,
  },
  {
    id: "register-item",
    trigger: "register item",
    title: "Register Inventory Item",
    description: "Add a new inventory item to the system",
    keywords: ["register", "add", "inventory", "item", "stock", "new"],
    category: "create",
    action: () =>
      router.navigate({
        to: "/inventory",
        state: { openDialog: "register-item" } as any,
      }),
    icon: Package,
  },
];

// Helper function to get all commands
export const getAllCommands = (
  router: ReturnType<typeof useRouter>,
  actions: Record<string, () => void> = {},
): SearchCommand[] => [
  ...createNavigationCommands(router),
  ...createCreationCommands(router),
  ...createAnnouncementCommands(),
];

// The latest announcement, on demand: anyone who missed it (or closed it too
// fast) can always find it again here.
const createAnnouncementCommands = (): SearchCommand[] => {
  const latest = latestAnnouncement(ANNOUNCEMENTS);
  if (!latest) return [];
  return [
    {
      id: "whats-new",
      trigger: "what's new",
      title: "What's new",
      description: latest.title,
      keywords: ["what's new", "whats new", "news", "update", "release", "announcement"],
      category: "action",
      action: () => useAnnouncementStore.getState().open(latest.id),
      icon: Sparkles,
    },
  ];
};

// Command categories for UI organization
export const commandCategories = {
  navigate: {
    title: "Navigate",
    description: "Go to different sections",
    icon: SearchIcon,
  },
  create: {
    title: "Create",
    description: "Create new items",
    icon: Plus,
  },
  action: {
    title: "Actions",
    description: "Perform quick actions",
    icon: FileText,
  },
} as const;
